import {
  alpha,
  darken as muiDarken,
  getContrastRatio as muiGetContrastRatio,
  lighten as muiLighten,
} from '@mui/material/styles';

export { alpha };

type Rgba = {
  red: number;
  green: number;
  blue: number;
  alpha: number;
};

type ContrastSafeColorOptions = {
  baseColor: string;
  backgrounds: readonly string[];
  mode: 'light' | 'dark';
  minimum?: number;
};

const parseChannel = (value: string): number => Number.parseFloat(value.trim());

const parseHexColor = (color: string): Rgba | null => {
  const hex = color.slice(1);
  const expanded =
    hex.length === 3 || hex.length === 4
      ? Array.from(hex, (character) => `${character}${character}`).join('')
      : hex;
  if (expanded.length !== 6 && expanded.length !== 8) return null;
  return {
    red: Number.parseInt(expanded.slice(0, 2), 16),
    green: Number.parseInt(expanded.slice(2, 4), 16),
    blue: Number.parseInt(expanded.slice(4, 6), 16),
    alpha: expanded.length === 8 ? Number.parseInt(expanded.slice(6, 8), 16) / 255 : 1,
  };
};

const parseColor = (color: string): Rgba | null => {
  if (color.startsWith('#')) return parseHexColor(color);
  const rgbaMatch = /^rgba?\(([^)]+)\)$/u.exec(color);
  if (!rgbaMatch) return null;
  const channels = rgbaMatch[1].split(',').map(parseChannel);
  if (channels.length < 3) return null;
  return {
    red: channels[0],
    green: channels[1],
    blue: channels[2],
    alpha: channels[3] ?? 1,
  };
};

const composite = (foreground: Rgba, background: Rgba): Rgba => ({
  red: foreground.red * foreground.alpha + background.red * (1 - foreground.alpha),
  green: foreground.green * foreground.alpha + background.green * (1 - foreground.alpha),
  blue: foreground.blue * foreground.alpha + background.blue * (1 - foreground.alpha),
  alpha: 1,
});

const getRelativeLuminance = ({ red, green, blue }: Rgba): number => {
  const [resolvedRed, resolvedGreen, resolvedBlue] = [red, green, blue].map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * resolvedRed + 0.7152 * resolvedGreen + 0.0722 * resolvedBlue;
};

export const darken = (color: string, coefficient: number): string => muiDarken(color, coefficient);

export const lighten = (color: string, coefficient: number): string =>
  muiLighten(color, coefficient);

export const getContrastRatio = (foreground: string, background: string): number => {
  const foregroundColor = parseColor(foreground);
  const backgroundColor = parseColor(background);
  if (!foregroundColor || !backgroundColor || backgroundColor.alpha < 1) {
    return muiGetContrastRatio(foreground, background);
  }
  const resolvedForeground =
    foregroundColor.alpha < 1 ? composite(foregroundColor, backgroundColor) : foregroundColor;
  const foregroundLuminance = getRelativeLuminance(resolvedForeground);
  const backgroundLuminance = getRelativeLuminance(backgroundColor);
  return (
    (Math.max(foregroundLuminance, backgroundLuminance) + 0.05) /
    (Math.min(foregroundLuminance, backgroundLuminance) + 0.05)
  );
};

export const hasMinimumContrast = (
  color: string,
  backgrounds: readonly string[],
  minimum = 4.5,
): boolean => backgrounds.every((background) => getContrastRatio(color, background) >= minimum);

export const resolveContrastSafeColor = ({
  baseColor,
  backgrounds,
  mode,
  minimum = 4.5,
}: ContrastSafeColorOptions): string => {
  if (hasMinimumContrast(baseColor, backgrounds, minimum)) return baseColor;
  const adjust = mode === 'dark' ? lighten : darken;
  const steps = mode === 'dark' ? [0.2, 0.3, 0.4, 0.5, 0.6] : [0.1, 0.2, 0.3, 0.4, 0.5];
  for (const step of steps) {
    const candidate = adjust(baseColor, step);
    if (hasMinimumContrast(candidate, backgrounds, minimum)) return candidate;
  }
  return baseColor;
};

export const pickReadableTextColor = (
  background: string,
  fallback: string,
  black = '#000000',
  white = '#ffffff',
  minimum = 4.5,
): string => {
  if (getContrastRatio(fallback, background) >= minimum) return fallback;
  const blackContrast = getContrastRatio(black, background);
  const whiteContrast = getContrastRatio(white, background);
  if (blackContrast >= minimum || whiteContrast >= minimum) {
    return blackContrast >= whiteContrast ? black : white;
  }
  return fallback;
};
